import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Grid,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  AuditLogFilter,
  AuditLogSummaryDto,
  PagedResult,
} from "../interfaces/auditLog";
import { getAuditLogs } from "../api/auditLogService";
import useAuth from "../hooks/useAuth";
import { format } from "date-fns";
import { exportToExcel } from "../helpers/exportToExcel";

const LogsPage: React.FC = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState<PagedResult<AuditLogSummaryDto>>({
    totalCount: 0,
    items: [],
  });
  const [filter, setFilter] = useState<AuditLogFilter>({
    page: 1,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        const result = await getAuditLogs(token, filter);
        setLogs(result);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [token, filter]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setFilter((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFilter((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      page: 1,
    }));
  };

  const handleFilterChange =
    (field: keyof AuditLogFilter) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilter((prev) => ({
        ...prev,
        [field]: event.target.value,
        page: 1,
      }));
    };

  const formatChangeValue = (value: unknown) => {
    if (value === null || value === undefined) return "null";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const formatChangesForExport = (log: AuditLogSummaryDto) => {
    const allChangedFields = new Set([
      ...(log.oldValues ? Object.keys(log.oldValues) : []),
      ...(log.newValues ? Object.keys(log.newValues) : []),
    ]);

    if (allChangedFields.size === 0) return "";

    return Array.from(allChangedFields)
      .map((field) => {
        const oldValue = formatChangeValue(log.oldValues?.[field]);
        const newValue = formatChangeValue(log.newValues?.[field]);
        return `${field}: ${oldValue} → ${newValue}`;
      })
      .join("\n");
  };

  const exportRows = useMemo(
    () =>
      logs.items.map((log) => ({
        Date: format(new Date(log.performedAtUtc), "yyyy-MM-dd HH:mm:ss"),
        User: log.performedBy,
        Table: log.tableName,
        Action: log.actionType,
        Changes: formatChangesForExport(log),
      })),
    [logs.items],
  );

  const handleExport = () => {
    exportToExcel(exportRows, "AuditLogs");
  };

  const renderValueChanges = (log: AuditLogSummaryDto) => {
    const allChangedFields = new Set([
      ...(log.oldValues ? Object.keys(log.oldValues) : []),
      ...(log.newValues ? Object.keys(log.newValues) : []),
    ]);

    if (allChangedFields.size === 0) return "-";

    return (
      <TableContainer role="region" aria-label="Audit log table" tabIndex={0}>
        <Table sx={{ minWidth: 900 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Field</TableCell>
              <TableCell>Old Value</TableCell>
              <TableCell>New Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(allChangedFields).map((field) => (
              <TableRow key={field}>
                <TableCell>{field}</TableCell>
                <TableCell sx={{ color: "error.main" }}>
                  {log.oldValues?.[field] ?? "null"}
                </TableCell>
                <TableCell sx={{ color: "success.main" }}>
                  {log.newValues?.[field] ?? "null"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Audit Logs
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Table"
              value={filter.table || ""}
              onChange={handleFilterChange("table")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="User"
              value={filter.user || ""}
              onChange={handleFilterChange("user")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="From"
              value={filter.from || ""}
              onChange={handleFilterChange("from")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="To"
              value={filter.to || ""}
              onChange={handleFilterChange("to")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Box
        sx={{
          flexWrap: { xs: "wrap", lg: "nowrap" },
          display: "flex",
          justifyContent: "flex-end",
          mb: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleExport}
          disabled={loading || exportRows.length === 0}
        >
          Export to Excel
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <TableContainer role="region" aria-label="Audit log table" tabIndex={0}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Table</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Changes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : (
                logs.items.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {format(
                        new Date(log.performedAtUtc),
                        "yyyy-MM-dd HH:mm:ss",
                      )}
                    </TableCell>
                    <TableCell>{log.performedBy}</TableCell>
                    <TableCell>{log.tableName}</TableCell>
                    <TableCell>{log.actionType}</TableCell>
                    <TableCell>{renderValueChanges(log)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={logs.totalCount}
          page={filter.page! - 1}
          onPageChange={handlePageChange}
          rowsPerPage={filter.pageSize!}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>
    </Box>
  );
};

export default LogsPage;
